<script setup lang="ts">
import { computed, onMounted } from 'vue'
import MarkdownIt from 'markdown-it'
import { NButton, NCollapse, NCollapseItem, NEmpty, NSpin, useMessage } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import { useRetrievalBenchStore } from '../stores/retrieval_bench'
import { useDocumentTitle } from '../composables/useDocumentTitle'

// Admin-only view of the retrieval-benchmark Tier 1 summary.
// Surfaces the committed `docs/retrieval_bench.md` report — P@5 / R@5 /
// MRR scores + the Problems section — so an admin can read the numbers in
// the SPA instead of opening the file on the box, and re-run the
// benchmark on demand via the "Run benchmark" button.
//
// Auth gating happens at the router level (see router/index.ts); this
// view trusts that gate. The run is a background job: the button POSTs
// to enqueue it, the report refreshes when the RetrievalBenchChannel
// ping lands (driven by the store's subscription).

const store = useRetrievalBenchStore()
const message = useMessage()

useDocumentTitle('Retrieval benchmark')

// Plain markdown-it — NOT the wiki renderer. The report's Problems
// section lists fixture paths (e.g. `concepts/prompt-caching.md`) that
// don't exist in the live wiki, so the wiki renderer's `.md` autolinking
// would mint dead `/p/…` links. `linkify: false` keeps those paths as
// inert text; `html: false` is defence-in-depth even though the content
// is our own generated report.
const md = new MarkdownIt({ html: false, linkify: false, breaks: false })

const renderedHtml = computed(() => (store.content ? md.render(store.content) : ''))

async function run() {
  try {
    await store.triggerRun()
    message.success('Benchmark queued — the report refreshes when the run finishes.')
  } catch (err) {
    message.error(err instanceof Error ? err.message : 'Failed to start benchmark')
  }
}

onMounted(() => {
  store.subscribe()
  if (!store.loaded) void store.fetch()
})
</script>

<template>
  <RouteShell>
    <section class="max-w-3xl mx-auto flex flex-col gap-6 pb-6">
      <header class="flex flex-col gap-3">
        <div class="flex items-start justify-between gap-4">
          <div class="flex flex-col gap-1">
            <h1 class="page-title">Retrieval benchmark</h1>
            <p class="text-sm text-white/60 m-0">
              Fixture-driven P@5 / R@5 / MRR scores for <code class="text-white/50">Wiki::Retrieval</code>, scored
              against a fixed corpus of gold-labelled questions.
            </p>
          </div>
          <NButton type="primary" :loading="store.running" data-testid="retrieval-bench-run" @click="run">
            Run benchmark
          </NButton>
        </div>

        <!-- Metrics explainer. Collapsed by default so it doesn't crowd
             the scores, but always present (even before the first run)
             so an admin can learn what the numbers mean up front. -->
        <NCollapse>
          <NCollapseItem title="What do these numbers mean?" name="metrics">
            <dl class="flex flex-col gap-2 text-sm text-white/70 m-0">
              <div>
                <dt class="text-white/90 font-medium">P@5 — Precision@5</dt>
                <dd class="m-0">
                  Of the top 5 pages retrieved per question, the share that were actually relevant. 1.0 means every
                  top-5 result was on-target; lower means more noise. It caps below 1.0 for questions with fewer than 5
                  relevant pages.
                </dd>
              </div>
              <div>
                <dt class="text-white/90 font-medium">R@5 — Recall@5</dt>
                <dd class="m-0">
                  Of all the pages that <em>should</em> be found for a question, the share that appeared in the top 5.
                  1.0 means nothing relevant was missed.
                </dd>
              </div>
              <div>
                <dt class="text-white/90 font-medium">MRR — Mean Reciprocal Rank</dt>
                <dd class="m-0">
                  Averages 1 ÷ (rank of the first relevant result) across questions. 1.0 means the top hit is always
                  relevant; 0.5 means it's typically second; 0 means no relevant page surfaced at all.
                </dd>
              </div>
            </dl>
          </NCollapseItem>
        </NCollapse>
      </header>

      <div v-if="store.runError" class="text-red-300 text-sm" data-testid="retrieval-bench-run-error">
        {{ store.runError }}
      </div>

      <!-- Branch precedence: in-flight → error → empty → content. Keyed
           off `loading` (not `loaded`) so a failed fetch lights the error
           branch instead of trapping a perpetual spinner — same contract
           as UsageView. -->
      <div v-if="store.loading" class="flex items-center justify-center py-12">
        <NSpin />
      </div>

      <div v-else-if="store.loadError" class="text-red-300 text-sm" data-testid="retrieval-bench-load-error">
        Failed to load: {{ store.loadError }}
      </div>

      <NEmpty
        v-else-if="!store.content"
        description="No benchmark report yet — click Run benchmark to generate one."
        class="my-12"
      />

      <article v-else class="wiki-markdown text-sm" data-testid="retrieval-bench-report" v-html="renderedHtml" />
    </section>
  </RouteShell>
</template>
